/**
 * Hazard kind contract + the shared node mask (FS-06 §3.1, §4).
 *
 * Every kind's SDF ends with the same node carve, so the node guarantee is one function tested once
 * rather than twelve near-copies. `max(dBody, dNode)` means the node OVERRIDES the body: inside the
 * node the returned distance is the distance to the node's edge, which makes the node passable by
 * construction. Intensity never touches `nodeHalfTheta` (FS-06 R12) — that is the line that carries
 * Pillar P3.
 */
import { TUNNEL_RADIUS } from '../../core/constants';
import { angularDelta, dsin } from '../../core/math-det';
import type { HazardKind } from '../../core/event-map';

export const NODE_RELIEF_CAP = 3.0; // u
export const RING_THICKNESS = 0.55; // u
export const SLAB_INSET = 0.3; // u

// ── Amendment PT-1 (FS-06 §5.5) ────────────────────────────────────────────
/** Clearance above SHIP_HITBOX_RADIUS required at `nodeRMin` (R38). */
export const NODE_RADIAL_MARGIN = 0.25; // u
/**
 * Absolute floor on `nodeRMin`. Derived as `(SHIP_HITBOX_RADIUS + NODE_RADIAL_MARGIN) / minHalfTheta`
 * — and it is exactly the radius that used to be permanently safe. The number that was the defect
 * is now the number that bounds the fix.
 */
export const NODE_RMIN_FLOOR = 1.6; // u
/** Rim kinds were already at full reach; the range exists for symmetry. */
export const REACH_RIM_MIN = 0.85;
export const REACH_RIM_MAX = 1.0;
/**
 * An axis hazard at full reach leaves the node as the only passage anywhere on the disc; pairing
 * that with the next beat's rim hazard is the unavoidable death P3 forbids. 0.80 leaves an
 * unthreatened annulus for a player crossing polarities. FS-08 R35's beat spacing is the other
 * half of the same argument.
 */
/**
 * Radial width of a node's band (FS-06 R38). The node is an arc segment *at a radius*: a ship must
 * reach the right bearing AND the right radius. Wide enough to be comfortably passable at
 * `MAX_LATERAL_SPEED`; narrow enough that "anywhere along this bearing" is no longer safe, which is
 * what made mid-radius camping possible even after polarity landed.
 */
export const NODE_BAND_WIDTH = 4.0; // u

export const REACH_AXIS_MIN = 0.55;
export const REACH_AXIS_MAX = 0.95;

/** Design Bible §7.2. Polarity is a per-hazard parameter, never a thirteenth kind. */
export type HazardPolarity = 'rim' | 'axis';
export type RadialClass = 'rim' | 'axis' | 'full';

export interface HazardParams {
  amp: number;
  freq: number;
  phase: number;
  nodeTheta: number;
  nodeHalfTheta: number;
  /** Node radial band (FS-06 R38). The node is an arc *segment at a radius*, not a wedge. */
  nodeRMin: number;
  nodeRMax: number;
  /** Which side the lethal material grows from (Design Bible §7.2). */
  polarity: HazardPolarity;
  /** How far the material crosses the disc, 0..1. The R40 difficulty channel. */
  reach: number;
  count: number;
  rate: number;
  seed: number;
}

export type Silhouette =
  | 'continuous-curve'
  | 'hard-step'
  | 'ramp-teeth'
  | 'linear-peaks'
  | 'standing-lobes'
  | 'concentric-ring'
  | 'radial-bars'
  | 'solid-slab'
  | 'stochastic-field'
  | 'twin-helix'
  | 'converging-sweep'
  | 'nested-echo';

export interface HazardKindDef {
  readonly kind: HazardKind;
  /** Which side the material grows from by default (Design Bible §7.2, FS-06 R34). */
  readonly radialClass: RadialClass;
  /** Polarities the chart may ask this kind for. `PulseRing`/`FeedbackLoop` are rim-only (R41). */
  readonly polarities: readonly HazardPolarity[];
  /** Pure, closed-form, allocation-free. Negative inside the lethal region. */
  signedDistance(p: HazardParams, x: number, y: number, tLocal: number): number;
  /** For rendering the inner silhouette: radius of the lethal material's inner edge at angle θ. */
  innerRadius(p: HazardParams, theta: number, tLocal: number): number;
  readonly zNear: number;
  readonly zFar: number;
  readonly telegraphLead: number; // s, ≥ 0.9 at every tier (Pillar P2)
  readonly silhouette: Silhouette;
  readonly nodeCount: number;
  /** Node θ at a given tLocal — non-constant for moving-node kinds (FS-06 R24). */
  nodeThetaAt?(p: HazardParams, tLocal: number): number;
  /** The only path by which difficulty reaches a hazard (FS-06 R28). Never touches nodeHalfTheta. */
  scaleByIntensity(intensity: number, p: HazardParams): void;
  /** Analytic minimum node arc width, world units. */
  nodeWidthAt(intensity: number, p: HazardParams): number;
  /** Palette hint (FS-11 owns real palettes); shape+outline carry identity, hue is secondary. */
  readonly hue: number;
}

/** θ convention: θ = 0 at +Y, increasing toward +X. */
export function toTheta(x: number, y: number): number {
  let a = Math.atan2(x, y);
  if (a < 0) a += Math.PI * 2;
  return a;
}

/**
 * Shared node carve, **banded in radius** (FS-06 R38, Amendment PT-1).
 *
 * The arc-wise relief is `(halfTheta − a) · r`, which goes to zero as `r → 0`: an angular gap has
 * no width on the axis. That is correct for a full-disc kind — it is why `SubBassSlab` kills a
 * centre-parked ship — and fatal for an axis-polarity kind, whose node must be *reachable*. So the
 * node carries a radial band and the relief is the lesser of the two distances to its edges.
 *
 * With `rMin = 0`, `rMax = PLAY_RADIUS` this reduces exactly to the original behaviour, which is
 * why the band could be landed as a no-op before any kind changed.
 */
export function nodeMask(
  theta: number,
  nodeTheta: number,
  nodeHalfTheta: number,
  r: number,
  rMin = 0,
  rMax = Infinity,
): number {
  const a = Math.abs(angularDelta(theta, nodeTheta));
  if (a >= nodeHalfTheta) return -Infinity;
  if (r < rMin || r > rMax) return -Infinity;
  const dArc = (nodeHalfTheta - a) * r;
  const dBand = Math.min(r - rMin, rMax - r);
  const relief = dArc < dBand ? dArc : dBand;
  return relief < NODE_RELIEF_CAP ? relief : NODE_RELIEF_CAP;
}

/** Compose a kind's body distance with the shared node carve at the (static) chart node. */
export function withNode(dBody: number, theta: number, p: HazardParams, r: number): number {
  const dNode = nodeMask(theta, p.nodeTheta, p.nodeHalfTheta, r, p.nodeRMin, p.nodeRMax);
  return dBody > dNode ? dBody : dNode;
}

/** As `withNode`, but at an explicit (possibly moving) node θ. */
export function withNodeAt(
  dBody: number,
  theta: number,
  nodeTheta: number,
  nodeHalfTheta: number,
  r: number,
  rMin = 0,
  rMax = Infinity,
): number {
  const dNode = nodeMask(theta, nodeTheta, nodeHalfTheta, r, rMin, rMax);
  return dBody > dNode ? dBody : dNode;
}

/**
 * The polarity mirror (FS-06 R36) — one shape, two signs.
 *
 * `rim`  : material hangs from the tunnel wall inward to `surface`; safe below it.
 * `axis` : the same surface mirrored about the tunnel's mid-radius, material growing from the axis
 *          outward; safe above it.
 *
 * `reach` scales how far the material crosses the disc, which is the difficulty channel that
 * replaces "narrow the node" (R40). It never touches a node field.
 */
export function bodyDistance(p: HazardParams, surface: number, r: number): number {
  if (p.polarity === 'axis') {
    const mirrored = TUNNEL_RADIUS - surface;
    const reached = mirrored * p.reach;
    return r - reached;
  }
  const reached = TUNNEL_RADIUS + (surface - TUNNEL_RADIUS) * p.reach;
  return reached - r;
}

export { TUNNEL_RADIUS, dsin };
